import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SearchBar } from './components/SearchBar';
import { Menu } from './components/Menu';
import { DisclaimerModal } from './components/DisclaimerModal';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { Imprint } from './components/Imprint';
import { About } from './components/About';
import { AddCustomFoodModal } from './components/AddCustomFoodModal';
import { RecipeModal } from './components/RecipeModal';
import { IOSInstallModal } from './components/IOSInstallModal';
import { InstallAppButton } from './components/InstallAppButton';
import { PreferencesProvider, usePreferences } from './contexts/PreferencesContext';
import { Food, FoodDatabase, CarbUnit } from './types';
import './App.css';

interface DatabaseLoadingState {
  name: string;
  loaded: boolean;
  loading: boolean;
  error: string | null;
  size?: number;
}

function AppContent() {
  const [foods, setFoods] = useState<Food[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [databaseStatus, setDatabaseStatus] = useState<DatabaseLoadingState[]>([]);
  const databaseStatusInitialized = useRef(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateRequested, setUpdateRequested] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showImprint, setShowImprint] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showAddCustomFood, setShowAddCustomFood] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showIOSInstallModal, setShowIOSInstallModal] = useState(false);
  const { preferences, updatePreference, getCurrentRecipeIngredients } = usePreferences();
  const databasesLoaded = useRef(false);
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [progressBarFading, setProgressBarFading] = useState(false);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(false);

  // Progressive database loading function - memoized to prevent re-creation
  const loadDatabase = useCallback(async (url: string, dbIndex: number, isEssential: boolean = false) => {
    setDatabaseStatus(prev => prev.map((db, i) => 
      i === dbIndex ? { ...db, loading: true } : db
    ));
    
    try {
      const response = await fetch(url);
      const data: FoodDatabase = await response.json();
      
      // Add source information to each food
      const foodsWithSource = data.foods.map(food => ({
        ...food,
        source: data.source,
        sourceDE: data.sourceDE,
        sourceUrl: data.sourceUrl
      }));
      
      // Add to existing foods array
      setFoods(prevFoods => [...prevFoods, ...foodsWithSource]);
      
      // Update database status
      setDatabaseStatus(prev => prev.map((db, i) => 
        i === dbIndex ? { 
          ...db, 
          loaded: true, 
          loading: false, 
          size: data.foods.length,
          error: null 
        } : db
      ));
      
      console.log(`Loaded ${data.source}: ${data.foods.length} foods`);
      
      
    } catch (err) {
      console.error(`Failed to load database ${url}:`, err);
      // Better error messages
      let errorMessage = 'Failed to load';
      if (!navigator.onLine) {
        // If offline, the error could be because the file isn't cached yet
        errorMessage = 'Not cached (offline)';
      }
      
      setDatabaseStatus(prev => prev.map((db, i) => 
        i === dbIndex ? { 
          ...db, 
          loading: false, 
          error: errorMessage
        } : db
      ));
      
      // If essential database fails and we're offline, check if we have cached data
      if (isEssential && !navigator.onLine) {
        // Try to check if we have any cached foods data from previous sessions
        const hasExistingData = foods.length > 0;
        if (!hasExistingData) {
          setError('You are offline and no food data is cached. Please connect to the internet to load the app for the first time.');
          setLoading(false);
          return;
        } else {
          console.log('Using cached food data while offline');
        }
      }
      
      // If essential database fails but we're online, continue anyway
      if (isEssential) {
        console.warn('Essential database failed to load, but continuing...');
      }
    }
  }, [foods.length]);

  // Database loading - separate useEffect with empty dependencies to run only once
  useEffect(() => {
    if (databasesLoaded.current) {
      return; // Prevent duplicate loading in React strict mode
    }
    databasesLoaded.current = true;

    // Initialize database status based on language preference
    const isGerman = preferences.language === 'de';
    const initialStatus = [
      { name: 'Swiss Food', loaded: false, loading: false, error: null },
      { name: 'USDA Survey', loaded: false, loading: false, error: null },
      { name: isGerman ? 'Open Food Facts (DE)' : 'Open Food Facts (EN)', loaded: false, loading: false, error: null },
      { name: 'Open Food Facts (DE+EN)', loaded: false, loading: false, error: null }
    ];
    setDatabaseStatus(initialStatus);
    databaseStatusInitialized.current = true;

    // Load databases with progressive strategy - only run once
    const loadAllDatabases = async () => {
      // Phase 1: Load only essential Swiss Food database
      await loadDatabase('/swiss_food.json', 0, true);
      
      // App is ready to use after Swiss Food loads (or fails)
      setLoading(false);
      
      // Phase 2: Load additional databases in background
      // Always attempt to load - service worker will serve from cache if offline
      // Load USDA survey database in background
      loadDatabase('/usda_survey.json', 1, false);
      
      // Load Open Food Facts databases based on user's language preference
      if (isGerman) {
        // For German users: load German-only foods, then bilingual foods
        loadDatabase('/openfoodfacts_de.json', 2, false);
        loadDatabase('/openfoodfacts_de_en.json', 3, false);
      } else {
        // For English users: load bilingual foods only
        loadDatabase('/openfoodfacts_de_en.json', 3, false);
      }
    };
    
    loadAllDatabases();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  // Watch for database loading completion to trigger fade timer
  useEffect(() => {
    if (databaseStatus.length === 0) return; // Wait for initialization
    
    const allLoaded = databaseStatus.every(db => db.loaded || db.error);
    if (allLoaded && showProgressBar) {
      console.log('All databases loaded, starting 5 second fade timer');
      // Clear any existing timeout
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
      // Start fade timer
      fadeTimeoutRef.current = setTimeout(() => {
        console.log('Starting progress bar fade animation');
        setProgressBarFading(true);
        // Hide completely after animation finishes
        setTimeout(() => {
          setShowProgressBar(false);
          setProgressBarFading(false);
        }, 500); // Animation duration
      }, 3000);
    }
  }, [databaseStatus, showProgressBar]);

  useEffect(() => {
    // Check if disclaimer has been shown before
    const disclaimerShown = localStorage.getItem('disclaimer-shown');
    if (!disclaimerShown) {
      setShowDisclaimer(true);
    }

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsAppInstalled(true);
    }

    // Listen for PWA install prompt
    const handleInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Register service worker with update handling
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('Service Worker registered');
          
          // Check if there's already a waiting worker (page opened with update ready)
          if (registration.waiting) {
            console.log('Update already waiting on page load');
            setUpdateAvailable(true);
          }
          
          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('New content is available');
                  setUpdateAvailable(true);
                }
              });
            }
          });
        })
        .catch(err => console.error('Service Worker registration failed:', err));
      
      // Handle controller change (new SW activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Service Worker controller changed');
        // Reload if user has requested the update
        if (updateRequested) {
          window.location.reload();
        }
      });
    }

    // Handle online/offline events
    const handleOnline = () => {
      setIsOffline(false);
      
      // When coming back online, try to load any databases that failed due to being offline
      const failedDatabases = databaseStatus.filter((db, index) => 
        db.error === 'Offline' && !db.loaded
      );
      
      if (failedDatabases.length > 0) {
        console.log('Back online - retrying failed database loads');
        
        const isGerman = preferences.language === 'de';
        
        failedDatabases.forEach((db, originalIndex) => {
          const dbIndex = databaseStatus.findIndex(d => d.name === db.name);
          if (dbIndex === 0) { // Swiss Food
            loadDatabase('/swiss_food.json', dbIndex, true);
          } else if (dbIndex === 1) { // USDA Survey
            loadDatabase('/usda_survey.json', dbIndex, false);
          } else if (dbIndex === 2) { // Language-specific Open Food Facts
            if (isGerman) {
              loadDatabase('/openfoodfacts_de.json', dbIndex, false);
            } else {
              // English users will use the bilingual database at index 3
              return;
            }
          } else if (dbIndex === 3) { // Open Food Facts (DE+EN) - now at index 3
            loadDatabase('/openfoodfacts_de_en.json', dbIndex, false);
          }
        });
      }
      
      // Check if service worker has cached versions of large files
      if ('serviceWorker' in navigator && 'caches' in window) {
        caches.open('zuckerrechner-data-v9').then(cache => {
          const largeFiles = ['/usda_survey.json', '/openfoodfacts_de.json', '/openfoodfacts_de_en.json'];
          largeFiles.forEach(file => {
            cache.match(file).then(response => {
              if (!response) {
                console.log('Large file not in cache, will cache on next request:', file);
              }
            });
          });
        }).catch(() => {
          console.log('Cache API not available');
        });
      }
    };
    
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [updateRequested, databaseStatus, loadDatabase, preferences.language]);

  const handleUnitChange = (unit: CarbUnit) => {
    updatePreference('carbUnit', unit);
  };

  const toggleDarkMode = () => {
    updatePreference('darkMode', !preferences.darkMode);
  };

  const toggleLanguage = () => {
    updatePreference('language', preferences.language === 'de' ? 'en' : 'de');
  };

  const toggleShowFPU = (show: boolean) => {
    updatePreference('showFPU', show);
  };

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const handleDisclaimerClose = () => {
    setShowDisclaimer(false);
    localStorage.setItem('disclaimer-shown', 'true');
  };

  const handleShowDisclaimer = () => {
    setShowDisclaimer(true);
  };

  const handleShowPrivacyPolicy = () => {
    setShowPrivacyPolicy(true);
  };

  const handleShowImprint = () => {
    setShowImprint(true);
  };

  const handleShowAbout = () => {
    setShowAbout(true);
  };

  const handleShowIOSInstallInstructions = () => {
    setShowIOSInstallModal(true);
  };

  const handleReturnToHome = () => {
    setHasSearched(false);
    setClearSearchTrigger(true);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearComplete = () => {
    setClearSearchTrigger(false);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }


  const handleUpdate = async () => {
    if ('serviceWorker' in navigator) {
      try {
        // Get the registration
        const registration = await navigator.serviceWorker.getRegistration();
        
        if (registration && registration.waiting) {
          // Mark that update was requested by user
          setUpdateRequested(true);
          
          // Tell the waiting service worker to activate
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // iOS Safari workaround: force reload after a short delay
          // since controllerchange event is unreliable on iOS
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
          if (isIOS) {
            // Give the service worker time to activate, then reload
            setTimeout(() => {
              console.log('iOS detected: forcing reload for update');
              window.location.reload();
            }, 1000);
          }
        } else {
          // Fallback: just reload the page to get the latest version
          console.log('No waiting worker found, reloading page...');
          window.location.reload();
        }
      } catch (error) {
        console.error('Error updating service worker:', error);
        // Fallback: reload the page
        window.location.reload();
      }
    } else {
      // No service worker support, just reload
      window.location.reload();
    }
  };

  return (
    <div className="App">
      {!hasSearched && (
        <InstallAppButton
          canInstallApp={!!installPrompt && !isAppInstalled}
          isAppInstalled={isAppInstalled}
          onInstallApp={handleInstallApp}
          onShowIOSInstallInstructions={handleShowIOSInstallInstructions}
          language={preferences.language}
        />
      )}
      {updateAvailable && (
        <div className="update-banner">
          <span>A new version is available!</span>
          <button onClick={handleUpdate} className="update-button">Update</button>
        </div>
      )}
      {/* Floating Action Buttons Container */}
      <div className="fab-container">
        {isOffline && (
          <div className="fab offline-indicator" title={preferences.language === 'de' ? 'Offline-Modus' : 'Offline mode'}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.64 7c-.45-.34-4.93-4-11.64-4C5.28 3 .81 6.66.36 7L12 21.5L23.64 7zM3.53 7.5c.77-.77 2.53-2.5 8.47-2.5s7.7 1.73 8.47 2.5L12 17.5L3.53 7.5z"/>
              <path d="M8.5 12l3.5 3.5L15.5 12"/>
              <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
        )}
        
        {getCurrentRecipeIngredients().length > 0 && (
          <div className="fab recipe-fab" onClick={() => setShowRecipeModal(true)} title={preferences.language === 'de' ? 'Rezept anzeigen' : 'View recipe'}>
            <svg viewBox="0 0 24 24" className="recipe-fab-icon">
              <path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 0 0 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
            </svg>
            <div className="recipe-fab-badge">
              {getCurrentRecipeIngredients().length}
            </div>
          </div>
        )}
        
        <div className="fab add-food-fab" onClick={() => setShowAddCustomFood(true)} title={preferences.language === 'de' ? 'Eigenes Lebensmittel hinzufügen' : 'Add custom food'}>
          <svg viewBox="0 0 24 24" className="add-icon">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
        </div>
      </div>
      
      {hasSearched ? (
        <header className="app-header">
          <div className="header-content clickable-header" onClick={handleReturnToHome}>
            <img src="/logo96.png" alt="Logo" className="header-logo" />
            <h1 className="app-title">Zuckerrechner <span className="beta-label">beta</span></h1>
          </div>
          <div className="top-controls">
            <Menu
              preferences={preferences}
              onLanguageChange={toggleLanguage}
              onDarkModeToggle={toggleDarkMode}
              canInstallApp={!!installPrompt && !isAppInstalled}
              isAppInstalled={isAppInstalled}
              onInstallApp={handleInstallApp}
              onShowDisclaimer={handleShowDisclaimer}
              onShowAbout={handleShowAbout}
              onShowIOSInstallInstructions={handleShowIOSInstallInstructions}
            />
          </div>
        </header>
      ) : (
        <div className="top-controls-landing">
          <Menu
            preferences={preferences}
            onLanguageChange={toggleLanguage}
            onDarkModeToggle={toggleDarkMode}
            canInstallApp={!!installPrompt && !isAppInstalled}
            isAppInstalled={isAppInstalled}
            onInstallApp={handleInstallApp}
            onShowDisclaimer={handleShowDisclaimer}
            onShowAbout={handleShowAbout}
            onShowIOSInstallInstructions={handleShowIOSInstallInstructions}
          />
        </div>
      )}

      <main className={`app-main ${hasSearched ? 'has-searched' : 'landing'}`}>
        {!hasSearched && (
          <div className="landing-content">
            <img src="/logo192.png" alt="Zuckerrechner Logo" className="landing-logo" />
            <h1 className="landing-title">Zuckerrechner <span className="beta-label">beta</span></h1>
          </div>
        )}
        <SearchBar 
          foods={foods} 
          preferences={preferences}
          onUnitChange={handleUnitChange}
          onSearchStarted={() => {
            setHasSearched(true);
            // Scroll to top when search starts
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onSearchCleared={() => {
            setHasSearched(false);
            // Scroll to top when returning to landing
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onShowFPUChange={toggleShowFPU}
          clearSearch={clearSearchTrigger}
          onClearComplete={handleClearComplete}
        />
      </main>
      
      <DisclaimerModal 
        isOpen={showDisclaimer}
        onClose={handleDisclaimerClose}
        language={preferences.language}
      />
      
      {showPrivacyPolicy && (
        <PrivacyPolicy 
          language={preferences.language}
          onClose={() => setShowPrivacyPolicy(false)}
        />
      )}
      
      {showImprint && (
        <Imprint 
          language={preferences.language}
          onClose={() => setShowImprint(false)}
        />
      )}
      
      {showAbout && (
        <About 
          language={preferences.language}
          onClose={() => setShowAbout(false)}
        />
      )}
      
      <AddCustomFoodModal 
        isOpen={showAddCustomFood}
        onClose={() => setShowAddCustomFood(false)}
      />
      
      <RecipeModal 
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
      />
      
      <IOSInstallModal
        isOpen={showIOSInstallModal}
        onClose={() => setShowIOSInstallModal(false)}
        language={preferences.language}
      />
      
      {/* Database Loading Progress Bar */}
      {showProgressBar && databaseStatus.length > 0 && (
        <div className={`database-progress-bar ${progressBarFading ? 'fading' : ''}`}>
          <div className="progress-bar-container">
            <div className="progress-bar-label">
              {databaseStatus.every(db => db.loaded || db.error)
                ? databaseStatus.some(db => db.error)
                  ? (preferences.language === 'de' ? 'Datenbank-Laden abgeschlossen' : 'Database loading completed')
                  : (preferences.language === 'de' ? 'Datenbanken geladen' : 'Databases loaded')
                : (preferences.language === 'de' ? 'Lade Datenbanken...' : 'Loading databases...')
              }
            </div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ 
                  width: `${(databaseStatus.filter(db => db.loaded || db.error).length / databaseStatus.length) * 100}%` 
                }}
              />
            </div>
            <div className="progress-text">
              {databaseStatus.filter(db => db.loaded || db.error).length}/{databaseStatus.length}
            </div>
          </div>
        </div>
      )}
      
      
      <div className="legal-links">
        <button 
          className="legal-link"
          onClick={handleShowAbout}
        >
          {preferences.language === 'de' ? 'Über Zuckerrechner' : 'About Zuckerrechner'}
        </button>
        <span className="legal-separator">•</span>
        <button 
          className="legal-link"
          onClick={handleShowPrivacyPolicy}
        >
          {preferences.language === 'de' ? 'Datenschutz' : 'Privacy'}
        </button>
        <span className="legal-separator">•</span>
        <button 
          className="legal-link"
          onClick={handleShowImprint}
        >
          {preferences.language === 'de' ? 'Impressum' : 'Imprint'}
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <PreferencesProvider>
      <AppContent />
    </PreferencesProvider>
  );
}

export default App;